import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import { adminAPI } from '../../services/api';
import { formatDateTimeIST } from '../../utils/dealFilters';
import PaginationBar from '../../components/common/PaginationBar';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false,
  });

  const fetchUsers = async (page = 0, term = '', role = '') => {
    setLoading(true);
    try {
      const response = await adminAPI.searchUsers(term, page, pagination.pageSize, role || undefined);
      // BasePageDTO is the response body (axios: response.data)
      const payload = response?.data?.data && !Array.isArray(response.data.data) && response.data.totalPages == null
        ? response.data.data
        : response.data;
      const list = Array.isArray(payload?.data)
        ? payload.data
        : (Array.isArray(payload?.content) ? payload.content : (Array.isArray(payload) ? payload : []));
      const totalItems = Number(payload?.totalElements ?? payload?.totalItems ?? list.length) || 0;
      const pageSize = Number(payload?.size ?? payload?.perPage ?? pagination.pageSize ?? 10) || 10;
      const totalPages = Number(payload?.totalPages) || Math.max(1, Math.ceil(totalItems / pageSize));
      const currentPage = Number(payload?.pageNumber ?? page) || 0;
      setUsers(list);
      setPagination((prev) => ({
        ...prev,
        currentPage,
        totalPages,
        totalItems,
        pageSize,
        hasNext: Boolean(payload?.hasNext ?? currentPage < totalPages - 1),
        hasPrevious: Boolean(payload?.hasPrevious ?? currentPage > 0),
      }));
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(0, searchTerm, roleFilter);
  };

  const handleUnlock = async (user) => {
    try {
      await adminAPI.unlockUser(user.id);
      toast.success(`Account unlocked for ${user.email}`);
      fetchUsers(pagination.currentPage, searchTerm, roleFilter);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to unlock user');
    }
  };

  return (
    <div style={{ padding: '24px', flex: 1, backgroundColor: '#f8fafc' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Users</h1>
            <p style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>Manage buyer, seller, mediator, and admin login accounts.</p>
          </div>
        </div>

        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search email, username, phone"
            style={{ flex: 1, minWidth: '220px', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
          />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', minWidth: '140px' }}
          >
            <option value="">All Roles</option>
            <option value="BUYER">Buyer</option>
            <option value="MEDIATOR">Mediator</option>
            <option value="SELLER">Seller</option>
            <option value="ADMIN">Admin</option>
          </select>
          <Button type="submit">Search</Button>
        </form>

        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '12px', color: '#64748b' }}>User</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '12px', color: '#64748b' }}>Role</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '12px', color: '#64748b' }}>Phone</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '12px', color: '#64748b' }}>Created (IST)</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '12px', color: '#64748b' }}>Status</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '12px', color: '#64748b' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding: '40px', textAlign: 'center' }}>Loading users...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No users found</td></tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: '600', color: '#1e293b' }}>{user.username || user.email}</div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>{user.email}</div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>{user.role}</td>
                    <td style={{ padding: '14px 16px' }}>{user.phoneNumber || '-'}</td>
                    <td style={{ padding: '14px 16px', fontSize: '13px' }}>{formatDateTimeIST(user.creationDate)}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: user.enabled ? '#059669' : '#64748b' }}>
                          {user.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                        {user.accountLocked && (
                          <span style={{ fontSize: '11px', fontWeight: '600', color: '#b91c1c' }}>Login Locked</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      {user.accountLocked ? (
                        <button
                          type="button"
                          onClick={() => handleUnlock(user)}
                          style={{
                            padding: '6px 12px',
                            background: '#fef3c7',
                            color: '#92400e',
                            border: '1px solid #fde68a',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                          }}
                        >
                          Unlock
                        </button>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div style={{ padding: '8px 16px', borderTop: '1px solid #e2e8f0', background: '#fff' }}>
            <PaginationBar
              page={pagination.currentPage}
              totalPages={Math.max(1, pagination.totalPages || 1)}
              totalElements={pagination.totalItems}
              pageSize={pagination.pageSize || 10}
              onPageChange={(page) => fetchUsers(page, searchTerm, roleFilter)}
              showSummary
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
